import React, { Suspense, lazy, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router-dom';
// Components
import LoadingScreen from './components/utils/LoadingScreen';
// Constants
import {
  CONTACT_PAGE_URL,
  HOME_PAGE_URL,
  MAINTENANCE_PAGE_URL,
  POLICIES_PAGE_URL,
  ADMIN_CONTACT_FORM_PAGE_URL,
  ABOUT_PAGE_URL,
  LINKS_PAGE_URL,
  DOMINATION_GALLERY_PAGE_URL,
  ABDL_GALLERY_PAGE_URL,
  TOUR_PAGE_URL,
  HOW_TO_BOOK_PAGE_URL,
  DOM_FORM_PAGE_URL,
  ABDL_FORM_PAGE_URL,
  DOMINATION_SERVICES_PAGE_URL,
  ABDL_SERVICES_PAGE_URL,
  REVIEWS_PAGE_URL,
  LOGIN_PAGE_URL,
} from './utils/Routes';
import { CONSENT_KEY, COOKIE_TIMER } from './utils/Constants';
// Auth
import { AuthenticateAdmin } from './utils/user/AuthenticateUser';
// Pages
// Public
import HomePage from './pages/home/HomePage';
import ContactPage from './pages/contact/ContactPage';
import AboutPage from './pages/about/AboutPage';
import LinksPage from './pages/links/LinksPage';
// Admin
import ContactFormAdminPage from './pages/contact/admin/ContactFormAdminPage';
// Terms and conditions
import TermAndPoliciesPage from './pages/policies/TermAndPoliciesPage';
// Maintainance
import MaintenancePage from './pages/maintenance/MaintenancePage';
// Error
import Error404 from './pages/error/Error404';
import DominationGallery from './pages/galleries/DominationGallery';
import BabyGallery from './pages/galleries/BabyGallery';
import TourPage from './pages/tour/TourPage';
import HowToBook from './pages/booking/HowToBook';
import DominatrixForm from './pages/booking/DominatrixForm';
import BabyForm from './pages/booking/BabyForm';
import DominationServicesPage from './pages/services/DominationServicesPage';
import BabyServicesPage from './pages/services/BabyServicesPage';
import ReviewsPage from './pages/reviews/ReviewsPage';
import LoginPage from './users/login/LoginPage';
// Hooks
import useScrollToTopOnRouteChange from './utils/functions/ScrollToTop';

// Components
const CookieConsentModal = lazy(() =>
  import('./components/modals/CookieConsentModal')
);

function App() {
  const [isVisible, setIsVisible] = useState(false);
  const [hasConsented, setHasConsented] = useState(false);
  useScrollToTopOnRouteChange();

  useEffect(() => {
    const consent = localStorage.getItem(CONSENT_KEY);
    setHasConsented(!!consent);
  }, []);

  useEffect(() => {
    if (hasConsented) {
      setIsVisible(false);
      return;
    }

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, COOKIE_TIMER);

    return () => clearTimeout(timer);
  }, [hasConsented]);

  return (
    <>
      {isVisible && (
        <Suspense>
          <CookieConsentModal setHasConsented={setHasConsented} />
        </Suspense>
      )}

      {/* Suspense component wraps the lazy-loaded components */}
      <Suspense fallback={<LoadingScreen />}>
        <Routes>
          {/* Main page routes */}
          <Route path={HOME_PAGE_URL} index element={<HomePage />} />
          <Route path={ABOUT_PAGE_URL} element={<AboutPage />} />
          <Route path={LINKS_PAGE_URL} element={<LinksPage />} />
          <Route path={REVIEWS_PAGE_URL} element={<ReviewsPage />} />

          {/* Services */}
          <Route
            path={DOMINATION_SERVICES_PAGE_URL}
            element={<DominationServicesPage />}
          />
          <Route path={ABDL_SERVICES_PAGE_URL} element={<BabyServicesPage />} />

          {/* Booking */}
          <Route path={HOW_TO_BOOK_PAGE_URL} element={<HowToBook />} />
          <Route path={TOUR_PAGE_URL} element={<TourPage />} />
          <Route path={DOM_FORM_PAGE_URL} element={<DominatrixForm />} />
          <Route path={ABDL_FORM_PAGE_URL} element={<BabyForm />} />

          {/* Galleries */}
          <Route
            path={DOMINATION_GALLERY_PAGE_URL}
            element={<DominationGallery />}
          />
          <Route path={ABDL_GALLERY_PAGE_URL} element={<BabyGallery />} />

          {/* Contact routes */}
          <Route path={CONTACT_PAGE_URL} element={<ContactPage />} />

          {/* Contact admin routes */}
          <Route
            path={`${ADMIN_CONTACT_FORM_PAGE_URL}`}
            element={
              <AuthenticateAdmin>
                <ContactFormAdminPage />
              </AuthenticateAdmin>
            }
          />

          {/* User routes */}
          <Route path={LOGIN_PAGE_URL} element={<LoginPage />} />

          {/* Terms and conditions */}
          <Route path={POLICIES_PAGE_URL} element={<TermAndPoliciesPage />} />

          {/* Other */}
          <Route path={MAINTENANCE_PAGE_URL} element={<MaintenancePage />} />

          {/* Error routes */}
          <Route path='*' element={<Error404 />} />
        </Routes>
      </Suspense>
    </>
  );
}

export default App;
